import { Component, OnInit, signal, computed, inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// PrimeNG 21 Imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ResponsibleRegistryService } from '../../../services/responsible-registry.service';
import { Responsible } from '../../../models/responsible.model';
import { WizardStepService } from '../../../services/wizard-step.service';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { FormDropdownItem, FormDropdownService } from '../../../../core/services/form-dropdown.service';
import { NoSpecialCharsValidator } from '../../../../core/validators/no-special-chars.validator';
import { CityCountryFormatValidator } from '../../../../core/validators/city-country-format.validator';
import { ValidationErrorComponent } from '../../../../core/shared/validation-error/validation-error.component';

@Component({
  selector: 'app-responsible-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    MultiSelectModule,
    RadioButtonModule,
    CheckboxModule,
    ConfirmDialogModule,
    ToastModule,
    ValidationErrorComponent,
  ],
  providers: [MessageService],
  templateUrl: './responsible-form.component.html',
  styleUrl: './responsible-form.component.scss',
})
export class ResponsibleFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private titleService = inject(Title);
  private responsibleService = inject(ResponsibleRegistryService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private wizardStepService = inject(WizardStepService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private formDropdownService = inject(FormDropdownService);
  
  // Optional: per supportare l'uso come dialog dinamico
  public dialogRef = inject(DynamicDialogRef, { optional: true });
  public dialogConfig = inject(DynamicDialogConfig, { optional: true });

  // Form Group
  responsibleForm!: FormGroup;
  isLoading = signal(false);
  companyTypeOptions: FormDropdownItem[] = [];

  ngOnInit(): void {
    this.titleService.setTitle('Form Responsabile - PalEasy');

    this.initForm();
    this.loadCompanyTypes();
  }

  private loadCompanyTypes(): void {
    this.formDropdownService.getFormDropdownValues('CREATE_SUPPLIER', 'SUPPLIER_TYPE').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.companyTypeOptions = response.data;
        }
      },
      error: (err) => {
        console.error('Error loading company types:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i tipi societa',
          life: 3000,
        });
      },
    });
  }

  private initForm(): void {
    this.responsibleForm = this.fb.group({
      businessName: ['', [Validators.required, Validators.minLength(3), NoSpecialCharsValidator('free-text')]],
      companyType: ['', [Validators.required]],
      headquarters: ['', [Validators.required, NoSpecialCharsValidator('address'), CityCountryFormatValidator()]],
      email: ['', [Validators.required, Validators.email, NoSpecialCharsValidator('strict')]],
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Controlla se ci sono campi con caratteri speciali non permessi
   * @returns Array di nomi campi con errori
   */
  private hasSpecialCharsErrors(): string[] {
    const fieldsWithErrors: string[] = [];
    const fieldLabels: { [key: string]: string } = {
      businessName: 'Ragione Sociale',
      companyType: 'Tipo societa',
      headquarters: 'Sede',
      email: 'Email',
    };

    Object.keys(this.responsibleForm.controls).forEach(key => {
      const control = this.responsibleForm.get(key);
      if (control?.hasError('specialChars') && fieldLabels[key]) {
        fieldsWithErrors.push(fieldLabels[key]);
      }
    });

    return fieldsWithErrors;
  }

  submitForm(): void {
    // ✅ XSS VALIDATION: Controlla caratteri speciali PRIMA della validazione required
    const specialCharsFields = this.hasSpecialCharsErrors();
    if (specialCharsFields.length > 0) {
      this.responsibleForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }

    if (this.responsibleForm.invalid) {
      this.markFormGroupTouched(this.responsibleForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Completa tutti i campi obbligatori',
        life: 3000
      });
      return;
    }

    this.isLoading.set(true);

    // Applica null per valori vuoti
    const formValues = this.responsibleForm.value;
    const formData = Object.keys(formValues).reduce((acc, key) => {
      acc[key] = formValues[key] || null;
      return acc;
    }, {} as any) as Responsible;

    console.log('Form data:', formData);

    this.responsibleService.createResponsible(formData).subscribe({
      next: (response: any) => {
        this.isLoading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Responsabile registrato con successo',
          life: 3000
        });
        
        // Se aperto come dialog, chiudi e ritorna il responsabile creato
        if (this.dialogRef && response?.data) {
          const createdResponsible = Array.isArray(response.data) ? response.data[0] : response.data;
          this.dialogRef.close(createdResponsible);
        } else {
          // Altrimenti naviga al registro
          setTimeout(() => {
            this.router.navigate(['/axa/responsibles-registry']);
          }, 1500);
        }
      },
      error: (error: any) => {
        this.isLoading.set(false);
        console.error('Error creating manager:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile registrare il responsabile. Riprova più tardi.',
          life: 5000
        });
      }
    });
  }

  navigateToRegister(): void {
    // Se aperto come dialog, chiudi senza salvare
    if (this.dialogRef) {
      this.dialogRef.close(null);
    } else {
      // Altrimenti naviga al registro
      this.confirmDialogService.confirmNavigateToRegister('/axa/responsibles-registry');
    }
  }

  // Utility
  isFieldInvalid(form: FormGroup, fieldName: string): boolean {
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getErrorMessage(form: FormGroup, fieldName: string): string {
    const field = form.get(fieldName);
    if (field?.hasError('required')) {
      return 'Campo obbligatorio';
    }
    if (field?.hasError('minlength')) {
      return `Minimo ${field.errors?.['minlength'].requiredLength} caratteri`;
    }
    if (field?.hasError('email')) {
      return 'Email non valida';
    }
    return '';
  }
}
